import { DirectionPage, JsonLd, MetaData, SubDirectionPage, TextPage } from '@/components'
import { useHost } from '@/contexts/HostContext'
import calcCurrentDirection from '@/lib/calcCurrentDirection'
import { getCanonicalUrl } from '@/lib/locales'
import { buildDirectionSchemas } from '@/lib/schema'
import shouldNoIndexDirection from '@/lib/shouldNoIndexDirection'
import stripUnrenderedDirectionImages, { slimDirectionPayload } from '@/lib/stripUnrenderedDirectionImages'
import { useRouter } from 'next/router'

const SIMPLE_TEXT_PAGE_SLUGS = ['patient-and-family-bill-of-rights-and-responsibilities']

function getRequestSlug(typeSlug, directions = []) {
  const cur = calcCurrentDirection(typeSlug, directions, directions[0])
  return typeSlug !== cur?.slug
    ? typeSlug
    : cur?.children?.[0]?.slug || cur?.slug || typeSlug
}

async function fetchDirectionsBySlug(api, slug) {
  try {
    return await api.directions({ direction: slug })
  } catch {
    return {}
  }
}

export default function NextTypePage(props) {
  const { type, data, robots, typeSlug } = props;
  const { title, description } = data.meta || {};
  const isSimple = SIMPLE_TEXT_PAGE_SLUGS.includes(typeSlug || data.slug)
  const host = useHost()
  const router = useRouter()
  const pageUrl = getCanonicalUrl(router?.asPath || `/${data.slug || ''}`, host)
  const directionSchema =
    (type === 'direction' || type === 'subdirection') && !isSimple
      ? buildDirectionSchemas(data, { host, asPath: router?.asPath, pageUrl })
      : null

  switch (type) {
    case 'direction':
      if (isSimple) {
        return (
          <>
            <MetaData title={title || data.title} description={description} robots={robots} />
            <TextPage {...data} simple />
          </>
        )
      }

      return (
        <>
          <MetaData title={title || data.title} description={description} robots={robots} />
          <JsonLd data={directionSchema} />
          <DirectionPage {...data} />
        </>
      )
    case 'subdirection':
      return (
        <>
          <MetaData title={title || data.title} description={description} robots={robots} />
          <JsonLd data={directionSchema} />
          <SubDirectionPage {...data} />
        </>
      )
    case 'text_page':
      return (
        <>
          <MetaData title={title || data.title} description={description}/>
          <TextPage {...data} simple={isSimple} />
        </>
      )
  }
}

NextTypePage.getInitialProps = async ({ query, api }) => {
  const { typeSlug, ...otherQueries } = query
  const response = await api.type({ slug: typeSlug, ...otherQueries })
  const { page_type: type, data } = response

  if (type === 'direction' || type === 'subdirection') {
    const requestSlug = getRequestSlug(typeSlug, data.directions || [])
    const ownDirectionsPromise = SIMPLE_TEXT_PAGE_SLUGS.includes(typeSlug)
      ? Promise.resolve({})
      : fetchDirectionsBySlug(api, typeSlug)
    const servicesDataPromise =
      requestSlug === typeSlug
        ? ownDirectionsPromise
        : fetchDirectionsBySlug(api, requestSlug)

    const [directionsData, servicesData] = await Promise.all([
      ownDirectionsPromise,
      servicesDataPromise,
    ])

    const directions = directionsData.directions || data.directions || []
    const services = servicesData?.services || []
    const specialists = Array.isArray(directionsData.specialists)
      ? directionsData.specialists
      : Array.isArray(servicesData?.specialists)
        ? servicesData.specialists
        : []
    const current_direction = servicesData?.current_direction
    const short_content = directionsData.short_content || data.short_content
    // Учитываем и услуги текущего slug, и подставленные услуги первого ребёнка
    const robots = shouldNoIndexDirection({
      services,
      short_content,
      blocks: data.blocks,
    })
      ? 'noindex, follow'
      : undefined

    delete data.recommendations

    const isSimplePage = SIMPLE_TEXT_PAGE_SLUGS.includes(typeSlug || data.slug)
    if (!isSimplePage) {
      stripUnrenderedDirectionImages({
        data,
        directions,
        current_direction,
        services,
        directionsData,
      })

      if (servicesData && servicesData !== directionsData) {
        stripUnrenderedDirectionImages({
          directions: servicesData.directions,
          current_direction: servicesData.current_direction,
          services: servicesData.services,
          directionsData: servicesData,
        })
      }
    }

    if (isSimplePage) {
      return {
        type,
        pageType: 'direction',
        robots,
        data: {
          ...data,
          banners: directionsData.banners,
          directions,
          short_content,
          services,
          specialists,
          current_direction,
        },
      }
    }

    return {
      type,
      pageType: 'direction',
      robots,
      data: slimDirectionPayload({
        data,
        directions,
        services,
        specialists,
        current_direction,
        short_content,
      }),
    }
  }

  return { type, data }
}
